import { Trackable } from '@/components/common';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib';
import { AppDispatch, RootState } from '@/redux/store';
import { setCartData } from '@/redux/actions/user.action';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useDispatch } from 'react-redux';

interface GetTicketsBtnProps {
  eventStatus: "Upcoming" | "Ongoing" | "Ended" | "Unknown";
  ticketPriceDisplay: string;
  eventCode: string;
  onlineBookingStartText: string;
  onlineBookingEndText: string;
  isBookingClosed: boolean;
}

const GetTicketsBtn = ({
  eventCode,
  ticketPriceDisplay,
  onlineBookingStartText,
  onlineBookingEndText,
  isBookingClosed,
  eventStatus
}: GetTicketsBtnProps) => {
  const searchParams = useSearchParams();
  const code = searchParams.get('code');
  const dispatch = useDispatch<AppDispatch>();

  // Belt-and-suspenders: the buy-page itself already clears out a cart left
  // over from a different event, but doing it here too - right when the user
  // commits to booking THIS event - means there's never a moment where a
  // stale cross-event cart could be read before that check runs.
  const handleBookTickets = () => {
    dispatch((dispatchAction: AppDispatch, getState: () => RootState) => {
      const existingCart = getState()?.user?.cart;
      if (existingCart?.eventId && existingCart.eventId !== eventCode) {
        dispatchAction(setCartData({}, {}, {}, eventCode));
      }
    });
  };

  if (eventStatus === "Unknown") return null;
  if (eventStatus === "Upcoming") {
    return (
      <div className={cn('fixed sm:sticky md:relative bottom-0 cursor-pointer left-0 z-20 mt-4 flex flex-wrap w-full items-center justify-between overflow-hidden bg-primary p-0 text-white sm:p-4 sm:px-4 sm:pb-[6px]',)}>
        <div className='w-full rounded-lg p-4 flex items-center justify-center self-center '>
          <Label className='cursor-pointer text-base font-semibold tracking-normal uppercase'>{onlineBookingStartText || "Upcoming"}</Label>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('fixed sm:sticky md:relative bottom-0 cursor-pointer left-0 z-20 mt-4 flex flex-wrap w-full items-center justify-between overflow-hidden bg-primary p-4 text-white sm:p-4 sm:px-4 sm:pb-[6px]',)}>
      {eventStatus !== "Ended" ? (
        <>
          {!isBookingClosed && (
            <div className='relative z-10 flex flex-col py-1'>
              <Label className='text-xs font-semibold tracking-wide text-white/80 sm:text-xs'>
                STARTING FROM
              </Label>
              <Label className='sm:text-lg text-sm uppercase'>{ticketPriceDisplay}</Label>
            </div>
          )}

          <div className={cn('relative z-10 flex items-center gap-2 sm:pb-2', isBookingClosed && 'w-full')}>
            {isBookingClosed ? (
              <div className='w-full py-2 flex justify-end'>
                <span className='text-base font-semibold uppercase'>{onlineBookingEndText || "bookings closed"}</span>
              </div>
            ) : (
              <Trackable
                eventName='Click'
                eventScope='Click Get Tickets'
                eventProps={{
                  'Event Code': eventCode,
                }}
              >
                <Link
                  href={{
                    pathname: `/${eventCode}/buy-page`,
                    query: code && { code }
                  }}
                  prefetch={true}
                  onClick={handleBookTickets}
                  className='flex items-center gap-2'
                >
                  <div className='rounded-lg flex items-center gap-2'>
                    <Label className='cursor-pointer text-xs text-white font-semibold tracking-wide sm:tracking-normal sm:text-base uppercase'>Book Tickets</Label>
                    <ArrowRight
                      strokeWidth={3}
                      className='size-6 rounded-full bg-white p-1 text-primary'
                    />
                  </div>
                </Link>
              </Trackable>
            )}
          </div>
        </>
      ) : (
        <div className='w-full rounded-lg p-4 flex items-center justify-center self-center '>
          <Label className='cursor-pointer text-base font-semibold tracking-normal uppercase'>{onlineBookingEndText || "bookings closed"}</Label>
        </div>
      )}
    </div>
  );
}

export default GetTicketsBtn;
